(() => {
  if (window.CProductCardGallery) return;

  const gallerySelector = '[data-c-product-card-gallery]';
  const cardSelector = '.c-product-card[data-product-id]';
  const enhancedCardSelector = `${cardSelector}[data-variant-gallery="true"], ${cardSelector}[data-sibling-products="true"], ${cardSelector}[data-has-reviews="true"]`;
  const rapidCardSelector = `.rps-products-native-design ${cardSelector}`;
  const sourceCards = new Map();
  const sourceBadges = new Map();
  const productRequests = new Map();
  let emblaPromise;
  let scanFrame;
  let appFrame;

  function collectSourceCards() {
    if (!document.querySelector('.rps-results')) return;

    document.querySelectorAll('#ProductGridContainer').forEach((grid) => {
      if (grid.closest('.rps-results')) return;

      grid.querySelectorAll(cardSelector).forEach((card) => {
        const badges = card.querySelector('.c-product-card__badges');
        if (badges) sourceBadges.set(card.dataset.productId, badges.outerHTML);
      });

      grid.querySelectorAll(enhancedCardSelector).forEach((card) => {
        sourceCards.set(card.dataset.productId, card.outerHTML);
      });

      grid.replaceChildren();
    });
  }

  function loadEmbla() {
    if (typeof window.EmblaCarousel === 'function') return Promise.resolve(window.EmblaCarousel);
    if (emblaPromise) return emblaPromise;

    emblaPromise = new Promise((resolve, reject) => {
      let script = Array.from(document.scripts).find((item) =>
        item.src.includes('embla-carousel/embla-carousel.umd.js')
      );
      let created = false;

      const done = () =>
        typeof window.EmblaCarousel === 'function'
          ? resolve(window.EmblaCarousel)
          : reject(new Error('EmblaCarousel is unavailable.'));

      if (!script) {
        script = document.createElement('script');
        script.src = 'https://unpkg.com/embla-carousel/embla-carousel.umd.js';
        script.async = true;
        script.dataset.jckmLoader = 'EmblaCarousel';
        created = true;
      }

      script.addEventListener('load', done, { once: true });
      script.addEventListener('error', reject, { once: true });
      if (created) document.head.appendChild(script);
    }).catch((error) => {
      emblaPromise = undefined;
      throw error;
    });

    return emblaPromise;
  }

  function initializeGallery(gallery) {
    if (gallery.dataset.galleryInitialized) return;

    const viewport = gallery.querySelector('[data-gallery-viewport]');
    const slides = gallery.querySelectorAll('[data-gallery-slide]');
    const thumbnails = [...gallery.querySelectorAll('[data-gallery-thumbnail]')];

    if (!viewport || slides.length < 2) {
      gallery.dataset.galleryInitialized = 'static';
      return;
    }

    gallery.dataset.galleryInitialized = 'pending';

    loadEmbla()
      .then((EmblaCarousel) => {
        if (!gallery.isConnected) return;

        const embla = EmblaCarousel(viewport, { align: 'start', loop: false });
        const select = () => {
          const selected = embla.selectedScrollSnap();

          thumbnails.forEach((thumbnail) => {
            const active = Number(thumbnail.dataset.slideIndex) === selected;
            thumbnail.classList.toggle('is-active', active);
            thumbnail.setAttribute('aria-current', String(active));
          });
        };

        thumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => embla.scrollTo(Number(thumbnail.dataset.slideIndex)));
        });

        embla.on('select', select);
        gallery.dataset.galleryInitialized = 'true';
        select();
      })
      .catch((error) => {
        delete gallery.dataset.galleryInitialized;
        console.error('Product card gallery initialization failed.', error);
      });
  }

  function reinitializeApps() {
    if (appFrame) return;

    appFrame = requestAnimationFrame(() => {
      appFrame = undefined;
      window.HulkappWishlist?.init?.();
      window.jdgm?.initializeWidgets?.();
    });
  }

  function replaceFromSource(card) {
    const html = sourceCards.get(card.dataset.productId);
    if (!html) return false;

    const template = document.createElement('template');
    template.innerHTML = html.trim();

    const replacement = template.content.firstElementChild;
    if (!replacement) return false;

    replacement.dataset.rapidCardHydrated = 'source';
    replacement.querySelectorAll('[data-gallery-initialized]').forEach((gallery) => {
      delete gallery.dataset.galleryInitialized;
    });

    card.replaceWith(replacement);
    reinitializeApps();
    return true;
  }

  function replaceBadgesFromSource(card) {
    const html = sourceBadges.get(card.dataset.productId);
    const badges = card.querySelector('.c-product-card__badges');
    if (!html || !badges) return;

    badges.outerHTML = html;
  }

  function productEndpoint(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.pathname = url.pathname.endsWith('.js') ? url.pathname : `${url.pathname.replace(/\/$/, '')}.js`;
    url.search = '';
    return url.href;
  }

  function loadProduct(productUrl) {
    const url = productEndpoint(productUrl);
    if (productRequests.has(url)) return productRequests.get(url);

    const request = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        productRequests.delete(url);
        throw error;
      });

    productRequests.set(url, request);
    return request;
  }

  function hasColorOption(product) {
    return product.options?.some((option) => {
      const name = String(option.name || option).trim().toLowerCase();
      return name === 'color' || name === 'szín' || name === 'szin' || name === 'zsinórszín' || name === 'zsinór szín';
    });
  }

  function imageUrl(image, width) {
    const src = typeof image === 'string' ? image : image?.src;
    if (!src) return '';

    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', width);
    return url.href;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function variantUrl(productUrl, variantId) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    return url.href;
  }

  function rebuildGallery(card, product) {
    const items = [];
    const variantItems = [];
    const featuredSrc = imageUrl(product.featured_image, 800);

    if (product.variants?.length > 1) {
      product.variants.forEach((variant) => {
        const image = variant.featured_image || variant.featured_media?.preview_image;
        const src = imageUrl(image, 800);
        if (!src) return;

        variantItems.push({
          src,
          thumb: imageUrl(image, 88),
          url: variantUrl(card.dataset.productUrl, variant.id),
          alt: variant.title,
          isVariant: true,
        });
      });
    }

    const featuredMatchesVariant = variantItems.some((item) => item.src === featuredSrc);

    if (featuredSrc && !featuredMatchesVariant) {
      items.push({ src: featuredSrc, thumb: imageUrl(product.featured_image, 88), url: card.dataset.productUrl, alt: product.title });
    }

    items.push(...variantItems);

    if (!items.length) return;

    const slides = items
      .map(
        (item) => `
          <div class="c-product-card__gallery-slide" data-gallery-slide>
            <a href="${escapeHtml(item.url)}" class="c-product-card__image-link">
              <img src="${escapeHtml(item.src)}" class="c-product-card__image" loading="lazy" alt="${escapeHtml(item.alt)}">
            </a>
          </div>`
      )
      .join('');

    const thumbnailItems = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.isVariant);

    const thumbnails =
      items.length > 1 && thumbnailItems.length
        ? `<div class="c-product-card__thumbnails" data-gallery-thumbnails role="group" aria-label="Termékképek">
            ${thumbnailItems
              .map(
                ({ item, index }) => `
                  <button class="c-product-card__thumbnail${index === 0 ? ' is-active' : ''}" type="button" data-gallery-thumbnail data-slide-index="${index}" aria-label="${escapeHtml(item.alt)} képének megjelenítése" aria-current="${index === 0}">
                    <img src="${escapeHtml(item.thumb)}" class="c-product-card__thumbnail-image" loading="lazy" alt="">
                  </button>`
              )
              .join('')}
          </div>`
        : '';

    let gallery = card.querySelector(gallerySelector);

    if (!gallery) {
      gallery = document.createElement('div');
      gallery.className = 'c-product-card__gallery';
      gallery.dataset.cProductCardGallery = '';
      const media = card.querySelector('.c-product-card__media');
      media?.querySelector(':scope > .c-product-card__image-link')?.remove();
      media?.appendChild(gallery);
    }

    gallery.innerHTML = `
      <div class="c-product-card__gallery-viewport" data-gallery-viewport>
        <div class="c-product-card__gallery-container">${slides}</div>
      </div>
      ${thumbnails}`;
    delete gallery.dataset.galleryInitialized;
    initializeGallery(gallery);
  }

  function hydrateRapidCard(card) {
    if (card.dataset.rapidCardHydrated) return;
    if (replaceFromSource(card)) return;
    replaceBadgesFromSource(card);
    if (card.dataset.siblingProducts === 'true') {
      card.dataset.rapidCardHydrated = 'native';
      return;
    }
    if (!card.dataset.productUrl) return;
    if (card.dataset.variantsCount && Number(card.dataset.variantsCount) <= 1) {
      card.dataset.rapidCardHydrated = 'skipped';
      return;
    }

    card.dataset.rapidCardHydrated = 'pending';

    loadProduct(card.dataset.productUrl)
      .then((product) => {
        if (!card.isConnected) return;
        if (!hasColorOption(product)) {
          card.dataset.rapidCardHydrated = 'skipped';
          return;
        }
        rebuildGallery(card, product);
        card.dataset.rapidCardHydrated = 'ajax';
      })
      .catch((error) => {
        card.dataset.rapidCardHydrated = 'failed';
        console.error('Product card hydration failed.', error);
      });
  }

  function updateShowMoreCount() {
    const results = document.querySelector('.rps-results');
    const button = results?.querySelector('.rps-show-more-button');
    const count = results?.querySelector('.rps_results_hits') || results?.querySelector('.rps-result-count-wrapper-desktop');
    if (!button || !count) return;

    const total = Number(count.textContent.replace(/\D/g, ''));
    const remaining = Math.max(0, total - results.querySelectorAll(rapidCardSelector).length);
    const label = `Még több (${remaining})`;
    if (button.textContent !== label) button.textContent = label;
  }

  function updatePromoBanner() {
    const template = document.querySelector('[data-rapid-promo-banner-template]');
    const grid = document.querySelector('.rps-results .rps-products-native-design .jckm-grid');
    if (!template || !grid) return;

    const card = grid.querySelectorAll(cardSelector)[5];
    const banner = grid.querySelector('[data-rapid-promo-banner]');
    if (!card) {
      banner?.remove();
      return;
    }

    const item = card.closest('.jckm-grid__item');
    if (!item || item.parentElement !== grid || item.nextElementSibling === banner) return;

    banner?.remove();
    item.after(template.content.cloneNode(true));
    //console.log('Beszúrt banner:', grid.querySelector('[data-rapid-promo-banner]'));
  }

  function scan() {
    collectSourceCards();
    document.querySelectorAll(rapidCardSelector).forEach(hydrateRapidCard);
    document.querySelectorAll(gallerySelector).forEach(initializeGallery);
    updateShowMoreCount();
    updatePromoBanner();
  }

  function scheduleScan() {
    if (scanFrame) return;

    scanFrame = requestAnimationFrame(() => {
      scanFrame = undefined;
      scan();
    });
  }

  function start() {
    scan();
    new MutationObserver(scheduleScan).observe(document.getElementById('MainContent') || document.body, {
      childList: true,
      subtree: true,
    });
    document.addEventListener('shopify:section:load', scheduleScan);
  }

  window.CProductCardGallery = { scan: scheduleScan };
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', start, { once: true })
    : start();
})();
